import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Building, User, Mail, Phone, Globe, BookOpen, ChevronRight, ChevronLeft, CheckCircle } from '../../components/CustomIcons';
import LogoImg from '../../components/LogoImg';

const CATEGORIES = ['Higher Education', 'Technology Certifications', 'Trucking & Logistics', 'Healthcare', 'Skilled Trades', 'Business', 'Other'];
const STEPS = ['Organization Info', 'Programs Offered', 'Contact & Submit'];

export default function MarketplaceApply() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    org_name: '', category: '', website: '',
    contact_name: '', email: '', phone: '',
    description: '', courses_offered: '',
  });

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const validate = () => {
    const errs = {};
    if (step === 0) {
      if (!form.org_name.trim()) errs.org_name = 'Organization name is required';
      if (!form.category) errs.category = 'Please select a category';
    }
    if (step === 1) {
      if (!form.description.trim()) errs.description = 'Description is required';
      if (form.description.trim().length < 50) errs.description = 'Please provide at least 50 characters';
    }
    if (step === 2) {
      if (!form.contact_name.trim()) errs.contact_name = 'Contact name is required';
      if (!form.email.trim() || !form.email.includes('@')) errs.email = 'Valid email is required';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const next = () => { if (validate()) setStep(s => s + 1); };
  const back = () => setStep(s => s - 1);

  const submit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/marketplace/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (res.ok) setSubmitted(true);
      else {
        const err = await res.json();
        setErrors({ submit: err.detail || 'Submission failed. Please try again.' });
      }
    } catch (e) {
      setErrors({ submit: 'Network error. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-page)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
        <div style={{ maxWidth: '480px', textAlign: 'center' }} className="animate-fade-in">
          <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'var(--success-bg)', border: '2px solid var(--success)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <CheckCircle size={30} style={{ color: 'var(--success)' }} />
          </div>
          <h1 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '26px', color: 'var(--text-primary)', margin: '0 0 12px 0' }}>Application Submitted!</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '15px', lineHeight: 1.6, margin: '0 0 24px 0' }}>
            Thank you for applying to become an Assistiv Marketplace partner. Our team will review your application and respond within <strong>3–5 business days</strong>.
          </p>
          <Link to="/" className="btn-primary" style={{ textDecoration: 'none' }}>Return to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-page)', padding: '32px 24px' }}>
      <div style={{ maxWidth: '600px', margin: '0 auto' }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <Link to="/" style={{ textDecoration: 'none', display: 'inline-block' }}>
            <LogoImg height={48} alt="Assistiv OS" style={{ display: 'block', margin: '0 auto 8px' }} />
          </Link>
          <h1 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '24px', color: 'var(--text-primary)', margin: '12px 0 4px 0' }}>Become a Marketplace Partner</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px', margin: 0 }}>Deliver your courses to justice-impacted learners across facilities nationwide.</p>
        </div>

        {/* Step Indicator */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0', marginBottom: '28px' }}>
          {STEPS.map((s, i) => (
            <React.Fragment key={i}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: i < STEPS.length - 1 ? 'none' : 1 }}>
                <div style={{ width: '26px', height: '26px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: i < step ? 'var(--success)' : i === step ? 'var(--orange)' : 'var(--bg-elevated)', color: i <= step ? '#111111' : 'var(--text-muted)', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '12px', flexShrink: 0 }}>
                  {i < step ? '✓' : i + 1}
                </div>
                <span style={{ fontSize: '12px', color: i === step ? 'var(--text-primary)' : 'var(--text-muted)', fontWeight: i === step ? 600 : 400, fontFamily: 'Plus Jakarta Sans, sans-serif', whiteSpace: 'nowrap' }}>
                  {s}
                </span>
              </div>
              {i < STEPS.length - 1 && <div style={{ flex: 1, height: '2px', background: i < step ? 'var(--success)' : 'var(--border-default)', margin: '0 8px' }} />}
            </React.Fragment>
          ))}
        </div>

        <div className="card" style={{ padding: '28px' }}>

          {/* Step 0: Org Info */}
          {step === 0 && (
            <div>
              <h2 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '18px', color: 'var(--text-primary)', margin: '0 0 20px 0' }}>Organization Information</h2>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Organization Name *</label>
                <div style={{ position: 'relative' }}>
                  <Building size={15} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                  <input className="input-dark" value={form.org_name} onChange={e => set('org_name', e.target.value)}
                    placeholder="e.g. Dallas College" style={{ paddingLeft: '38px' }} data-testid="apply-org-name" />
                </div>
                {errors.org_name && <p style={{ color: 'var(--error)', fontSize: '12px', marginTop: '4px' }}>{errors.org_name}</p>}
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Organization Category *</label>
                <select className="input-dark" value={form.category} onChange={e => set('category', e.target.value)} data-testid="apply-category">
                  <option value="">Select a category...</option>
                  {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                </select>
                {errors.category && <p style={{ color: 'var(--error)', fontSize: '12px', marginTop: '4px' }}>{errors.category}</p>}
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Website (optional)</label>
                <div style={{ position: 'relative' }}>
                  <Globe size={15} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                  <input className="input-dark" value={form.website} onChange={e => set('website', e.target.value)}
                    placeholder="https://yourorganization.edu" style={{ paddingLeft: '38px' }} />
                </div>
              </div>
            </div>
          )}

          {/* Step 1: Programs */}
          {step === 1 && (
            <div>
              <h2 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '18px', color: 'var(--text-primary)', margin: '0 0 20px 0' }}>Programs Offered</h2>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Tell us about your organization and mission *</label>
                <textarea className="input-dark" value={form.description} onChange={e => set('description', e.target.value)}
                  placeholder="Describe your organization, your experience with workforce development, and why you want to partner with Assistiv OS..."
                  rows={5} style={{ resize: 'vertical', lineHeight: 1.6 }} data-testid="apply-description" />
                {errors.description && <p style={{ color: 'var(--error)', fontSize: '12px', marginTop: '4px' }}>{errors.description}</p>}
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Courses / Programs you plan to offer (optional)</label>
                <textarea className="input-dark" value={form.courses_offered} onChange={e => set('courses_offered', e.target.value)}
                  placeholder="e.g. GED Prep, Paralegal Certificate, CDL Class A, CompTIA A+..." rows={3} style={{ resize: 'vertical' }} />
              </div>
            </div>
          )}

          {/* Step 2: Contact */}
          {step === 2 && (
            <div>
              <h2 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '18px', color: 'var(--text-primary)', margin: '0 0 20px 0' }}>Contact Information</h2>
              {[
                { key: 'contact_name', label: 'Your Full Name *', icon: User, placeholder: 'Dr. Angela Torres' },
                { key: 'email', label: 'Email Address *', icon: Mail, placeholder: 'programs@yourorg.edu' },
                { key: 'phone', label: 'Phone Number (optional)', icon: Phone, placeholder: '(214) 555-0192' },
              ].map(({ key, label, icon: Icon, placeholder }) => (
                <div key={key} style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{label}</label>
                  <div style={{ position: 'relative' }}>
                    <Icon size={15} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                    <input className="input-dark" value={form[key]} onChange={e => set(key, e.target.value)}
                      placeholder={placeholder} style={{ paddingLeft: '38px' }}
                      data-testid={`apply-${key}`} />
                  </div>
                  {errors[key] && <p style={{ color: 'var(--error)', fontSize: '12px', marginTop: '4px' }}>{errors[key]}</p>}
                </div>
              ))}
              {errors.submit && (
                <div style={{ background: 'var(--error-bg)', border: '1px solid var(--error)', borderRadius: '8px', padding: '10px 14px', color: 'var(--error)', fontSize: '13px' }}>{errors.submit}</div>
              )}
            </div>
          )}
        </div>

        {/* Navigation */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px' }}>
          <button className="btn-secondary" onClick={back} disabled={step === 0} style={{ opacity: step === 0 ? 0.4 : 1 }}>
            <ChevronLeft size={16} /> Back
          </button>
          {step < STEPS.length - 1 ? (
            <button className="btn-primary" onClick={next} data-testid="apply-next-btn">
              Continue <ChevronRight size={16} />
            </button>
          ) : (
            <button className="btn-primary" onClick={submit} disabled={submitting} data-testid="apply-submit-btn">
              {submitting ? 'Submitting...' : 'Submit Application'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
